/**
 * Application Table Utility Functions
 * Pure utility functions for application data manipulation
 */

import { STATUS_BADGE_COLORS } from "./applicationConstants";

/**
 * Normalize application data from API
 * Ensures bArchive field is consistently boolean
 * @param {Object} app - Application object from API
 * @returns {Object} Normalized application object
 */
export const normalizeApplication = (app) => ({
  ...app,
  bArchive: Boolean(app.bArchive ?? app.barchive),
});

/**
 * Get count of applications by status (excluding archived)
 * @param {Array} applications - Array of application objects
 * @param {string} status - Status to count
 * @returns {number} Count of applications with given status
 */
export const getStatusCount = (applications, status) => {
  if (!applications || !Array.isArray(applications)) return 0;
  return applications.filter((a) => a.status === status && !a.bArchive).length;
};

/**
 * Get count of active (non-archived) applications
 * @param {Array} applications - Array of application objects
 * @returns {number} Count of active applications
 */
export const getActiveCount = (applications) => {
  if (!applications || !Array.isArray(applications)) return 0;
  return applications.filter((app) => !app.bArchive).length;
};

/**
 * Get count of archived applications
 * @param {Array} applications - Array of application objects
 * @returns {number} Count of archived applications
 */
export const getArchivedCount = (applications) => {
  if (!applications || !Array.isArray(applications)) return 0;
  return applications.filter((app) => app.bArchive).length;
};

/**
 * Get badge color for application status
 * @param {string} status - Application status
 * @returns {string} Chakra UI color scheme
 */
export const getStatusColor = (status) => {
  return STATUS_BADGE_COLORS[status?.toLowerCase()] || "gray";
};

/**
 * Get unique values from applications for filter dropdowns
 * @param {Array} applications - Array of application objects
 * @param {string} field - Field name to extract unique values from
 * @returns {Array} Array of unique values with "All" prepended
 */
export const getUniqueValues = (applications, field) => {
  if (!applications || !Array.isArray(applications)) return ["All"];
  return [
    "All",
    ...new Set(applications.map((app) => app[field]).filter(Boolean)),
  ];
};

/**
 * Filter applications based on criteria
 * @param {Array} applications - Array of application objects
 * @param {Object} filters - Filter criteria
 * @param {string} filters.statusFilter - Status filter
 * @param {string} filters.roleFilter - Role filter
 * @param {string} filters.locationFilter - Location filter
 * @param {string} filters.searchQuery - Search query
 * @returns {Array} Filtered applications
 */
export const filterApplications = (applications, filters) => {
  const {
    statusFilter = "All",
    roleFilter = "All",
    locationFilter = "All",
    searchQuery = "",
  } = filters;

  return applications.filter((app) => {
    // Status filter (includes archived special case)
    const statusOk =
      statusFilter === "archived"
        ? app.bArchive
        : statusFilter === "All"
          ? !app.bArchive
          : !app.bArchive && app.status === statusFilter;

    // Role filter
    const roleOk = roleFilter === "All" || app.roleTitle === roleFilter;

    // Location filter
    const locOk =
      locationFilter === "All" || app.jobLocationCity === locationFilter;

    // Search filter (searches company, role, location)
    const searchLower = searchQuery.toLowerCase();
    const searchOk =
      !searchQuery ||
      app.companyName?.toLowerCase().includes(searchLower) ||
      app.roleTitle?.toLowerCase().includes(searchLower) ||
      app.jobLocationCity?.toLowerCase().includes(searchLower);

    return statusOk && roleOk && locOk && searchOk;
  });
};

/**
 * Sort applications by field
 * @param {Array} applications - Array of application objects
 * @param {string} sortField - Field to sort by
 * @param {string} sortOrder - "asc" or "desc"
 * @returns {Array} Sorted applications
 */
export const sortApplications = (applications, sortField, sortOrder) => {
  return [...applications].sort((a, b) => {
    const aVal = a[sortField] ?? "";
    const bVal = b[sortField] ?? "";
    return sortOrder === "asc"
      ? aVal.toString().localeCompare(bVal.toString())
      : bVal.toString().localeCompare(aVal.toString());
  });
};

/**
 * Paginate applications
 * @param {Array} applications - Array of application objects
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} itemsPerPage - Items per page
 * @returns {Object} Paginated data with metadata
 */
export const paginateApplications = (
  applications,
  currentPage,
  itemsPerPage,
) => {
  const totalPages = Math.ceil(applications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = applications.slice(startIndex, startIndex + itemsPerPage);

  return {
    data: paginatedData,
    totalPages,
    startIndex,
    endIndex: startIndex + paginatedData.length,
    total: applications.length,
  };
};
